import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import msgConstants from '../../../../constants/msgConstants'
import { useConciliacaoContext } from '../../../../contexts/conciliacao-context'
import { PAGES } from '../../../../router/routes'
import ciclosManager from '../../../../services/api/ciclos/ciclosManager'
import parceiroManager from '../../../../services/api/parceiro/parceiroManager'
import { loadingReset, loadingUpdate } from '../../../../store/actions/loadingAction'
import { arquivoTemp, CicloStatus } from '../../../../types/ciclosApiType'
import { Parceiro } from '../../../../types/parceiroApiTypes'
import { CicloEtapaEnum } from '../../../../utils/enum'
import { base64FileToCsvArray, csvArrayToFile, getExtensaoArquivo } from '../../../../utils/uploadFileUtils'
import { stylesCicloInformacao } from './CicloInformacao.style'
import { concatenarFiltrosQuery, formatarData } from '../../../../utils/stringUtils'
import { CicloInformacaoProps } from './CicloInformacao.View'
import { TipoEnvioCiclo, TipoStatusExecucaoCiclo } from '../../../../types/execucaoCicloTypes'

export function cicloInformacaoIO({ cicloId, parceiroId, opsSucesso, statusCiclo, updateStatus, setEnableConfirmar }: CicloInformacaoProps) {
  const { cicloDados } = useConciliacaoContext()
  const [uploadButton, setUploadButton] = useState<boolean>(false)
  const [cicloEfetivado, setCicloEfetivado] = useState<boolean>(false)
  const [habilitarAtualizar, setHabilitarAtualizar] = useState<boolean>(false)
  const [parceiro, setParceiro] = useState<Parceiro>()
  const [modalCancelamentoAberto, setModalCancelamentoAberto] = useState(false)
  const [isAutomacao, setIsAutomacao] = useState<boolean>(false)
  const [mostrarAlertaExecucao, setMostrarAlertaExecucao] = useState<boolean>(false)
  const [mensagemAlerta, setMensagemAlerta] = useState<string>('')

  const dispatch = useDispatch()
  const router = useHistory()

  const getStatusTexto = (status: number): string => {
    const mapa: Record<number, string> = {
      [TipoStatusExecucaoCiclo.Novo]: 'Novo',
      [TipoStatusExecucaoCiclo.ErroAquivo]: 'Erro no Arquivo',
      [TipoStatusExecucaoCiclo.ErroValidacao]: 'Erro de Validação',
      [TipoStatusExecucaoCiclo.Validado]: 'Validado',
      [TipoStatusExecucaoCiclo.Divergente]: 'Divergente',
      [TipoStatusExecucaoCiclo.Rejeitado]: 'Rejeitado',
      [TipoStatusExecucaoCiclo.Aprovado]: 'Aprovado',
      [TipoStatusExecucaoCiclo.Sucesso]: 'Sucesso'
    }
    return mapa[status] ?? `${status}`
  }

  const getTipoEnvioTexto = (tipo: number): string => {
    switch (tipo) {
      case TipoEnvioCiclo.Planilha:
        return 'Planilha'
      case TipoEnvioCiclo.API:
        return 'API'
      default:
        return 'Desconhecido'
    }
  }

  const verificarExecucaoCiclo = useCallback(() => {
    if (statusCiclo && statusCiclo.tipoEnvio > 0) {
      const statusExecucao = statusCiclo.status
      const tipoEnvio = statusCiclo.tipoEnvio

      if (tipoEnvio === TipoEnvioCiclo.API) {
        setUploadButton(false)
        setMostrarAlertaExecucao(true)
        setMensagemAlerta(`Ciclo iniciado por API. Aguardando processamento automatizado. Status atual: ${getStatusTexto(statusExecucao)}`)
        return true
      }
      if (
        tipoEnvio === TipoEnvioCiclo.Planilha &&
        statusExecucao !== TipoStatusExecucaoCiclo.Aprovado &&
        statusExecucao !== TipoStatusExecucaoCiclo.Validado &&
        statusExecucao !== TipoStatusExecucaoCiclo.Sucesso
      ) {
        setUploadButton(false)
        setMostrarAlertaExecucao(true)

        if (statusExecucao === TipoStatusExecucaoCiclo.Divergente) {
          setMensagemAlerta('Há divergências que precisam ser resolvidas na tela de Validação de Execução.')
        } else {
          setMensagemAlerta(`Planilha já enviada. Aguardando processamento automatizado. Status atual: ${getStatusTexto(statusExecucao)}`)
        }
        return true
      } else if (tipoEnvio === TipoEnvioCiclo.Planilha) {
        setMostrarAlertaExecucao(false)
        return false
      }
    }
    return false
  }, [statusCiclo])

  const checkButton = useCallback(() => {
    const parceiroAutomacao = statusCiclo?.tipoEnvio > 0
    const regrasAutomacaoAplicadas = verificarExecucaoCiclo()

    if (parceiroAutomacao && regrasAutomacaoAplicadas) {
      const ultimaConciliacao = statusCiclo.conciliacao[0]
      if (
        ultimaConciliacao?.statusId === CicloEtapaEnum.conciliacaoFinanceiraSucesso ||
        ultimaConciliacao?.statusId === CicloEtapaEnum.conciliacaoLoteEfetivado
      ) {
        setCicloEfetivado(true)
      }
      return
    }

    let ultimoHistorico: CicloStatus

    if (statusCiclo.conciliacao.length > 0) {
      ultimoHistorico = statusCiclo.conciliacao[0]
    } else if (statusCiclo.validacao.length > 0) {
      ultimoHistorico = statusCiclo.validacao[0]
    } else if (statusCiclo.ops.length > 0) {
      ultimoHistorico = statusCiclo.ops[0]
    } else {
      ultimoHistorico = statusCiclo.internalizacao[0]

      ultimoHistorico.statusId === CicloEtapaEnum.aguardandoUploadPlanilha || ultimoHistorico.statusId === CicloEtapaEnum.uploadPlanilhaErros
        ? setUploadButton(true)
        : setUploadButton(false)
    }

    if (ultimoHistorico.statusTipo == 'SUCCESS' || ultimoHistorico.statusId === CicloEtapaEnum.validacaoConciliacaoCicloInconsistencia || opsSucesso === true) {
      setEnableConfirmar(true)
      if (ultimoHistorico.statusId === CicloEtapaEnum.conciliacaoFinanceiraSucesso || ultimoHistorico.statusId === CicloEtapaEnum.conciliacaoLoteEfetivado) {
        setCicloEfetivado(true)
      }
    }

    if (
      ultimoHistorico.statusId === CicloEtapaEnum.validacaoConciliacaoCicloSucesso ||
      ultimoHistorico.statusId === CicloEtapaEnum.validacaoConciliacaoCicloInconsistencia ||
      ultimoHistorico.statusId === CicloEtapaEnum.validacaoConciliacaoCicloErros ||
      ultimoHistorico.statusId === CicloEtapaEnum.validacaoConciliacaoCicloErroInesperado ||
      ultimoHistorico.statusId === CicloEtapaEnum.conciliacaoFinanceiraErros
    ) {
      setHabilitarAtualizar(true)
    } else {
      setHabilitarAtualizar(false)
    }
  }, [opsSucesso, setEnableConfirmar, statusCiclo, verificarExecucaoCiclo])

  useEffect(() => {
    async function buscarParceiro() {
      await parceiroManager
        .buscarDadoParceiro(parceiroId)
        .then((response: Parceiro) => {
          setParceiro(response)
          setIsAutomacao(response.isAutomacao === true || (statusCiclo && statusCiclo.tipoEnvio > 0))
        })
        .catch(() => {
          toast.error(msgConstants.INICIAR_CICLO.parceiro_inexistente)
          router.push(PAGES.ciclos)
        })
        .finally(() => {
          checkButton()
        })
    }

    if (parceiroId > 0) buscarParceiro()
  }, [checkButton, parceiroId, router, statusCiclo])

  const arquivoSelecionado = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const arquivo: File = event.target.files[0]
      const arquivoExtensao = getExtensaoArquivo(arquivo.name)

      const arquivoReader = new FileReader()

      if (arquivoExtensao != undefined) {
        arquivoReader.onloadend = () => {
          const arquivoArray: any[] = base64FileToCsvArray(arquivoReader.result as string)

          const contentType = 'text/csv'
          const novoArquivo = csvArrayToFile(arquivoArray, arquivo.name, contentType)

          dispatch(loadingUpdate(true))
          ciclosManager
            .uploadArquivoTemp(arquivoExtensao.name, cicloId, isAutomacao)
            .then((responseTemp: arquivoTemp) => {
              ciclosManager
                .uploadArquivoCiclo(responseTemp.url, novoArquivo, arquivoExtensao.value)
                .then(() => {
                  ciclosManager
                    .transicionarCiclo({ etapaCicloId: 'I', statusCicloId: 100 }, cicloId)
                    .then(() => {
                      if (isAutomacao && parceiroId && cicloId) {
                        const tipoEnvio = 1
                        ciclosManager
                          .enviarValidacaoAutomacao(cicloId, parceiroId, tipoEnvio)
                          .then(() => {
                            toast.success('Arquivo enviado para validação automação')
                            updateStatus()
                          })
                          .catch((error) => {
                            toast.error(error.message)
                            updateStatus()
                          })
                      } else {
                        ciclosManager
                          .processarArquivoTemp(arquivoExtensao.name, responseTemp.arquivoId ?? '', cicloId)
                          .then(() => {
                            updateStatus()
                            toast.success(msgConstants.ARQUIVO.processando)
                          })
                          .catch(() => {
                            toast.error(msgConstants.ARQUIVO.erroUpload)
                          })
                      }
                    })
                    .catch(() => {
                      toast.error(msgConstants.CICLO_FLUXO.erroTransicaoEtapa)
                    })
                })
                .catch(() => {
                  toast.error(msgConstants.ARQUIVO.erroUpload)
                })
                .finally(() => {
                  dispatch(loadingReset())
                })
            })
            .catch(() => {
              toast.error(msgConstants.ARQUIVO.erroUpload)
              dispatch(loadingReset())
            })
        }
        arquivoReader.readAsDataURL(arquivo)
      } else {
        toast.error(msgConstants.ARQUIVO.extensaoInvalida)
      }
    }
  }

  const cancelarCiclo = () => {
    ciclosManager
      .deletarCiclo(cicloId)
      .then(() => {
        setModalCancelamentoAberto(false)
        toast.success(msgConstants.CICLO_FLUXO.cicloCancelado)
        router.push(PAGES.ciclos)
      })
      .catch(() => {
        toast.error(msgConstants.CICLO_FLUXO.cicloCanceladoErro)
      })
  }

  const handleConfirmar = () => {
    const dataInicio = formatarData((cicloDados as any)?.dataInicialParceiro ?? cicloDados?.dataCadastro, '-') as string
    const dataFim = formatarData((cicloDados as any)?.dataFinalPrevistaParceiro ?? cicloDados?.dataCadastro, '-') as string
    const query = concatenarFiltrosQuery({ cicloId, dataInicio, dataFim, status: statusCiclo?.status })
    router.push(PAGES.validacaoEstruturaCicloParceiro(parceiroId) + query)
  }

  return {
    styles: stylesCicloInformacao,
    cicloDados,
    cicloEfetivado,
    parceiro,
    modalCancelamentoAberto,
    setModalCancelamentoAberto,
    cancelarCiclo,
    arquivoSelecionado,
    uploadButton,
    habilitarAtualizar,
    inputFileOptions: '.csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel',
    mostrarAlertaExecucao,
    mensagemAlerta,
    getStatusTexto,
    getTipoEnvioTexto,
    tipoEnvio: statusCiclo?.tipoEnvio,
    statusExecucao: statusCiclo?.status,
    handleConfirmar
  }
}

export type CicloInformacaoIO = ReturnType<typeof cicloInformacaoIO>
